import React, { useState, useEffect, useCallback, useRef } from 'react';
import styled, { keyframes } from 'styled-components';
import ToastrService from '../../../services/toaster.service';
import {
  ReactFlow,
  MiniMap,
  Controls,
  Background,
  useNodesState,
  useEdgesState,
  addEdge,
  MarkerType,
  Handle,
  Position,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import useAuthenticatedAxios from "../../../auth/AuthenticatedAxios";
import ImavenLoadingScreen from '../../../Components/Helpers/loading-screen';
import { useAIAssistant } from '../../../Components/AIAssistant';

// ============================================
// SVG ICONS (Simple, monochrome style)
// ============================================

const Icons = {
  play: <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><polygon points="5 3 19 12 5 21 5 3"/></svg>,
  question: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>,
  gitBranch: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="6" y1="3" x2="6" y2="15"/><circle cx="18" cy="6" r="3"/><circle cx="6" cy="18" r="3"/><path d="M18 9a9 9 0 0 1-9 9"/></svg>,
  file: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>,
  cpu: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="4" y="4" width="16" height="16" rx="2"/><rect x="9" y="9" width="6" height="6"/><line x1="9" y1="1" x2="9" y2="4"/><line x1="15" y1="1" x2="15" y2="4"/><line x1="9" y1="20" x2="9" y2="23"/><line x1="15" y1="20" x2="15" y2="23"/><line x1="20" y1="9" x2="23" y2="9"/><line x1="20" y1="14" x2="23" y2="14"/><line x1="1" y1="9" x2="4" y2="9"/><line x1="1" y1="14" x2="4" y2="14"/></svg>,
  checkCircle: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>,
  zap: <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>,
  nodes: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="5" cy="6" r="3"/><circle cx="19" cy="6" r="3"/><circle cx="12" cy="18" r="3"/><line x1="5" y1="9" x2="5" y2="12"/><line x1="19" y1="9" x2="19" y2="12"/><line x1="5" y1="12" x2="19" y2="12"/><line x1="12" y1="12" x2="12" y2="15"/></svg>,
  link: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg>,
  plus: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>,
  list: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/></svg>,
  trash: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>,
  save: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/></svg>,
  x: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>,
  sparkle: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2l2.4 7.2L22 12l-7.6 2.8L12 22l-2.4-7.2L2 12l7.6-2.8L12 2z"/></svg>,
  loader: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="2" x2="12" y2="6"/><line x1="12" y1="18" x2="12" y2="22"/><line x1="4.93" y1="4.93" x2="7.76" y2="7.76"/><line x1="16.24" y1="16.24" x2="19.07" y2="19.07"/><line x1="2" y1="12" x2="6" y2="12"/><line x1="18" y1="12" x2="22" y2="12"/><line x1="4.93" y1="19.07" x2="7.76" y2="16.24"/><line x1="16.24" y1="7.76" x2="19.07" y2="4.93"/></svg>,
  pin: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>,
  settings: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>,
  calendar: <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>,
};

// Spinning loader component for loading states
const SpinnerIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

// ============================================
// STYLED COMPONENTS
// ============================================

const ModuleContainer = styled.div`
  display: flex;
  height: 100%;
  width: 100%;
  gap: 0;
`;

const Sidebar = styled.div`
  width: 300px;
  min-width: 300px;
  height: 100%;
  background: rgba(15, 20, 35, 0.95);
  border-right: 1px solid rgba(99, 102, 241, 0.15);
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const SidebarHeader = styled.div`
  padding: 16px;
  border-bottom: 1px solid rgba(99, 102, 241, 0.15);
`;

const SidebarTitle = styled.h3`
  font-size: 13px;
  font-weight: 600;
  color: #a5b4fc;
  margin: 0 0 10px 0;
  text-transform: uppercase;
  letter-spacing: 1px;
`;

const SearchInput = styled.input`
  width: 100%;
  background: rgba(25, 30, 45, 0.8);
  border: 1px solid rgba(99, 102, 241, 0.2);
  border-radius: 8px;
  padding: 10px 12px;
  color: #fff;
  font-size: 13px;

  &:focus {
    outline: none;
    border-color: rgba(99, 102, 241, 0.5);
  }

  &::placeholder {
    color: rgba(255, 255, 255, 0.4);
  }
`;

const FlowsList = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 10px;

  /* Hide scrollbar but keep scroll functionality */
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE and Edge */
  &::-webkit-scrollbar {
    display: none; /* Chrome, Safari, Opera */
  }
`;

const FlowCard = styled.div`
  background: ${props => props.selected 
    ? 'rgba(99, 102, 241, 0.2)' 
    : 'rgba(25, 30, 45, 0.6)'};
  border: 1px solid ${props => props.selected 
    ? 'rgba(99, 102, 241, 0.5)' 
    : 'rgba(99, 102, 241, 0.15)'};
  border-radius: 10px;
  padding: 12px;
  margin-bottom: 8px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    border-color: rgba(99, 102, 241, 0.4);
    background: rgba(99, 102, 241, 0.1);
  }
`;

const FlowCardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 6px;
`;

const FlowCardTitle = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #fff;
`;

const FlowCardStatus = styled.span`
  font-size: 9px;
  padding: 2px 6px;
  border-radius: 8px;
  font-weight: 600;
  text-transform: uppercase;
  
  ${props => props.status === 'active' && `
    background: rgba(16, 185, 129, 0.15);
    color: #10b981;
  `}
  
  ${props => props.status === 'draft' && `
    background: rgba(251, 191, 36, 0.15);
    color: #fbbf24;
  `}
  
  ${props => props.status === 'archived' && `
    background: rgba(156, 163, 175, 0.15);
    color: #9ca3af;
  `}
  
  ${props => props.status === 'complete' && `
    background: rgba(99, 102, 241, 0.2);
    color: #818cf8;
  `}
`;

const FlowCardMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 10px;
  color: rgba(255, 255, 255, 0.4);
`;

const SidebarButtons = styled.div`
  padding: 12px;
  border-top: 1px solid rgba(99, 102, 241, 0.15);
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const CreateNewButton = styled.button`
  width: 100%;
  padding: 12px;
  background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
  border: none;
  border-radius: 8px;
  color: #fff;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    box-shadow: 0 4px 15px rgba(99, 102, 241, 0.4);
  }
`;

const FlowBuilderArea = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  background: rgba(10, 15, 25, 0.8);
  position: relative;
`;

// Workflow Progress Banner
const WorkflowBanner = styled.div`
  background: linear-gradient(135deg, rgba(99, 102, 241, 0.15) 0%, rgba(139, 92, 246, 0.1) 100%);
  border: 1px solid rgba(99, 102, 241, 0.3);
  border-radius: 8px;
  padding: 12px 16px;
  margin: 12px;
  display: flex;
  align-items: center;
  gap: 16px;
`;

const WorkflowSteps = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
`;

const WorkflowStep = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 600;
  transition: all 0.2s ease;
  
  ${props => props.$completed && `
    background: rgba(16, 185, 129, 0.2);
    color: #10b981;
  `}
  
  ${props => props.$current && !props.$completed && `
    background: rgba(251, 191, 36, 0.2);
    color: #fbbf24;
    animation: pulse 2s ease-in-out infinite;
  `}
  
  ${props => !props.$completed && !props.$current && `
    background: rgba(255, 255, 255, 0.05);
    color: rgba(255, 255, 255, 0.3);
  `}
  
  @keyframes pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.6; }
  }
  
  svg {
    width: 14px;
    height: 14px;
  }
`;

const WorkflowArrow = styled.span`
  color: rgba(255, 255, 255, 0.2);
  font-size: 16px;
`;

const WorkflowHint = styled.div`
  font-size: 11px;
  color: rgba(251, 191, 36, 0.9);
  background: rgba(251, 191, 36, 0.1);
  padding: 6px 12px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  gap: 6px;
  
  svg {
    width: 14px;
    height: 14px;
  }
`;

const FlowHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: rgba(15, 20, 35, 0.9);
  border-bottom: 1px solid rgba(99, 102, 241, 0.15);
`;

const FlowTitle = styled.input`
  background: transparent;
  border: none;
  font-size: 16px;
  font-weight: 600;
  color: #fff;
  width: 250px;

  &:focus {
    outline: none;
    border-bottom: 2px solid rgba(99, 102, 241, 0.5);
  }

  &::placeholder {
    color: rgba(255, 255, 255, 0.4);
  }
`;

const FlowActions = styled.div`
  display: flex;
  gap: 8px;
`;

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 7px 12px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  ${props => props.primary ? `
    background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
    border: none;
    color: #fff;
    
    &:hover {
      box-shadow: 0 4px 15px rgba(99, 102, 241, 0.4);
    }
  ` : props.danger ? `
    background: rgba(239, 68, 68, 0.1);
    border: 1px solid rgba(239, 68, 68, 0.3);
    color: #ef4444;
    
    &:hover {
      background: rgba(239, 68, 68, 0.2);
    }
  ` : `
    background: rgba(99, 102, 241, 0.1);
    border: 1px solid rgba(99, 102, 241, 0.3);
    color: #a5b4fc;
    
    &:hover {
      background: rgba(99, 102, 241, 0.2);
    }
  `}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

// Confirmation Modal animations and styles
const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const slideUp = keyframes`
  from { 
    opacity: 0;
    transform: translate(-50%, -40%);
  }
  to { 
    opacity: 1;
    transform: translate(-50%, -50%);
  }
`;

const ConfirmOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.75);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  animation: ${fadeIn} 0.2s ease-out;
`;

const ConfirmModal = styled.div`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: linear-gradient(135deg, rgba(25, 30, 45, 0.98) 0%, rgba(30, 35, 50, 0.98) 100%);
  border: 1px solid rgba(239, 68, 68, 0.4);
  border-radius: 16px;
  padding: 28px;
  width: 420px;
  max-width: 90%;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5), 0 0 40px rgba(239, 68, 68, 0.1);
  animation: ${slideUp} 0.25s ease-out;
`;

const ConfirmIcon = styled.div`
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: rgba(239, 68, 68, 0.15);
  border: 2px solid rgba(239, 68, 68, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 20px;
  
  svg {
    width: 28px;
    height: 28px;
    color: #ef4444;
  }
`;

const ConfirmTitle = styled.h3`
  font-size: 20px;
  font-weight: 600;
  color: #fff;
  margin: 0 0 12px 0;
  text-align: center;
`;

const ConfirmMessage = styled.p`
  font-size: 14px;
  color: rgba(255, 255, 255, 0.7);
  margin: 0 0 24px 0;
  text-align: center;
  line-height: 1.6;
`;

const ConfirmItemName = styled.span`
  color: #a5b4fc;
  font-weight: 600;
`;

const ConfirmButtons = styled.div`
  display: flex;
  gap: 12px;
  justify-content: center;
`;

const ConfirmButton = styled.button`
  padding: 12px 24px;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  min-width: 120px;

  ${props => props.$danger ? `
    background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
    border: none;
    color: #fff;
    
    &:hover {
      box-shadow: 0 4px 20px rgba(239, 68, 68, 0.4);
      transform: translateY(-1px);
    }
  ` : `
    background: rgba(255, 255, 255, 0.05);
    border: 1px solid rgba(255, 255, 255, 0.2);
    color: rgba(255, 255, 255, 0.8);
    
    &:hover {
      background: rgba(255, 255, 255, 0.1);
      border-color: rgba(255, 255, 255, 0.3);
    }
  `}
`;

const ReactFlowWrapper = styled.div`
  flex: 1;
  position: relative;
  
  .react-flow__node {
    border-radius: 8px;
    font-size: 12px;
  }

  .react-flow__edge-path {
    stroke-width: 2;
  }

  .react-flow__minimap {
    background: rgba(30, 41, 59, 0.95) !important;
    border: 1px solid rgba(99, 102, 241, 0.3);
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  }

  .react-flow__minimap-mask {
    fill: rgba(15, 23, 42, 0.85) !important;
  }

  /* Hide React Flow attribution or style it minimally */
  .react-flow__attribution {
    background: transparent !important;
    color: rgba(148, 163, 184, 0.3) !important;
    font-size: 9px !important;
    
    a {
      color: rgba(148, 163, 184, 0.3) !important;
    }
  }

  .react-flow__controls {
    border-radius: 8px;
    overflow: hidden;
    border: 1px solid rgba(99, 102, 241, 0.2);
    
    button {
      background: rgba(25, 30, 45, 0.9);
      border-bottom: 1px solid rgba(99, 102, 241, 0.2);
      
      &:hover {
        background: rgba(99, 102, 241, 0.2);
      }
      
      svg {
        fill: #a5b4fc;
      }
    }
  }
`;

// Toolbar at the top of the canvas
const Toolbar = styled.div`
  position: absolute;
  top: 10px;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(20, 25, 40, 0.95);
  border: 1px solid rgba(99, 102, 241, 0.3);
  border-radius: 10px;
  padding: 8px 12px;
  z-index: 100;
  display: flex;
  gap: 6px;
  align-items: center;
`;

const ToolbarDivider = styled.div`
  width: 1px;
  height: 24px;
  background: rgba(99, 102, 241, 0.3);
  margin: 0 6px;
`;

const ToolbarButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  padding: 8px 12px;
  background: ${props => props.active ? 'rgba(99, 102, 241, 0.3)' : 'rgba(25, 30, 45, 0.8)'};
  border: 1px solid ${props => props.active ? 'rgba(99, 102, 241, 0.5)' : 'rgba(99, 102, 241, 0.2)'};
  border-radius: 6px;
  color: ${props => props.active ? '#fff' : '#a5b4fc'};
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;

  &:hover {
    background: rgba(99, 102, 241, 0.2);
    border-color: rgba(99, 102, 241, 0.4);
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

// Node Properties Panel (Right side)
const PropertiesPanel = styled.div`
  width: 300px;
  min-width: 300px;
  height: 100%;
  background: rgba(15, 20, 35, 0.95);
  border-left: 1px solid rgba(99, 102, 241, 0.15);
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const PropertiesHeader = styled.div`
  padding: 16px;
  border-bottom: 1px solid rgba(99, 102, 241, 0.15);
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const PropertiesTitle = styled.h3`
  font-size: 14px;
  font-weight: 600;
  color: #a5b4fc;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const CloseButton = styled.button`
  background: rgba(239, 68, 68, 0.1);
  border: 1px solid rgba(239, 68, 68, 0.3);
  border-radius: 6px;
  color: #ef4444;
  padding: 6px 10px;
  font-size: 11px;
  cursor: pointer;
  
  &:hover {
    background: rgba(239, 68, 68, 0.2);
  }
`;

const PropertiesContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 16px;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(99, 102, 241, 0.3);
    border-radius: 4px;
  }
`;

const PropertyGroup = styled.div`
  margin-bottom: 16px;
`;

const PropertyLabel = styled.label`
  display: block;
  font-size: 11px;
  color: rgba(165, 180, 252, 0.7);
  margin-bottom: 6px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const PropertyInput = styled.input`
  width: 100%;
  background: rgba(25, 30, 45, 0.8);
  border: 1px solid rgba(99, 102, 241, 0.2);
  border-radius: 8px;
  padding: 10px 12px;
  color: #fff;
  font-size: 13px;

  &:focus {
    outline: none;
    border-color: rgba(99, 102, 241, 0.5);
  }

  &::placeholder {
    color: rgba(255, 255, 255, 0.4);
  }
`;

const PropertyTextarea = styled.textarea`
  width: 100%;
  background: rgba(25, 30, 45, 0.8);
  border: 1px solid rgba(99, 102, 241, 0.2);
  border-radius: 8px;
  padding: 10px 12px;
  color: #fff;
  font-size: 13px;
  resize: vertical;
  min-height: 80px;
  font-family: inherit;

  &:focus {
    outline: none;
    border-color: rgba(99, 102, 241, 0.5);
  }

  &::placeholder {
    color: rgba(255, 255, 255, 0.4);
  }
`;

const PropertySelect = styled.select`
  width: 100%;
  background: rgba(25, 30, 45, 0.8);
  border: 1px solid rgba(99, 102, 241, 0.2);
  border-radius: 8px;
  padding: 10px 12px;
  color: #fff;
  font-size: 13px;

  &:focus {
    outline: none;
    border-color: rgba(99, 102, 241, 0.5);
  }

  option {
    background: #1a1f2e;
  }
`;

const DeleteNodeButton = styled.button`
  width: 100%;
  padding: 12px;
  background: rgba(239, 68, 68, 0.1);
  border: 1px solid rgba(239, 68, 68, 0.3);
  border-radius: 8px;
  color: #ef4444;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  margin-top: 16px;

  &:hover {
    background: rgba(239, 68, 68, 0.2);
  }
`;

const EmptyState = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: rgba(255, 255, 255, 0.4);
  padding: 40px;
  text-align: center;
`;

const EmptyStateIcon = styled.div`
  font-size: 64px;
  margin-bottom: 20px;
  opacity: 0.5;
`;

const EmptyStateText = styled.div`
  font-size: 18px;
  margin-bottom: 10px;
`;

const EmptyStateSubtext = styled.div`
  font-size: 14px;
  opacity: 0.7;
`;

// Text to Flow Modal
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: rgba(20, 25, 40, 0.98);
  border: 1px solid rgba(99, 102, 241, 0.3);
  border-radius: 16px;
  padding: 24px;
  width: 600px;
  max-width: 90%;
  max-height: 80vh;
  overflow-y: auto;
`;

const ModalTitle = styled.h3`
  font-size: 20px;
  font-weight: 600;
  color: #fff;
  margin: 0 0 20px 0;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const ModalTextarea = styled.textarea`
  width: 100%;
  height: 200px;
  background: rgba(25, 30, 45, 0.8);
  border: 1px solid rgba(99, 102, 241, 0.2);
  border-radius: 10px;
  padding: 14px;
  color: rgba(255, 255, 255, 0.9);
  font-size: 14px;
  resize: none;
  font-family: inherit;
  margin-bottom: 16px;

  &:focus {
    outline: none;
    border-color: rgba(99, 102, 241, 0.5);
  }

  &::placeholder {
    color: rgba(255, 255, 255, 0.4);
  }
`;

const ModalInput = styled.input`
  width: 100%;
  background: rgba(25, 30, 45, 0.8);
  border: 1px solid rgba(99, 102, 241, 0.2);
  border-radius: 10px;
  padding: 12px 14px;
  color: rgba(255, 255, 255, 0.9);
  font-size: 14px;
  margin-bottom: 16px;

  &:focus {
    outline: none;
    border-color: rgba(99, 102, 241, 0.5);
  }

  &::placeholder {
    color: rgba(255, 255, 255, 0.4);
  }
`;

const ModalLabel = styled.label`
  display: block;
  font-size: 12px;
  color: rgba(165, 180, 252, 0.7);
  margin-bottom: 6px;
`;

const ModalButtons = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
`;

const NoPropertiesMessage = styled.div`
  text-align: center;
  padding: 40px 20px;
  color: rgba(255, 255, 255, 0.4);
  font-size: 13px;
`;

// ============================================
// CUSTOM NODE COMPONENTS WITH HANDLES
// ============================================

const nodeStyles = {
  start: { background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', borderColor: '#10b981' },
  question: { background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)', borderColor: '#3b82f6' },
  condition: { background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)', borderColor: '#f59e0b' },
  document: { background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)', borderColor: '#8b5cf6' },
  'ai-analysis': { background: 'linear-gradient(135deg, #ec4899 0%, #db2777 100%)', borderColor: '#ec4899' },
  outcome: { background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)', borderColor: '#6366f1' },
  action: { background: 'linear-gradient(135deg, #14b8a6 0%, #0d9488 100%)', borderColor: '#14b8a6' },
};

const nodeIcons = {
  start: Icons.play,
  question: Icons.question,
  condition: Icons.gitBranch,
  document: Icons.file,
  'ai-analysis': Icons.cpu,
  outcome: Icons.checkCircle,
  action: Icons.zap,
};

const NodeContainer = styled.div`
  padding: 12px 16px;
  border-radius: 8px;
  min-width: 160px;
  max-width: 220px;
  color: #fff;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.3);
  border: 2px solid ${props => props.borderColor || '#6366f1'};
  background: ${props => props.background || 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)'};
  cursor: pointer;

  &:hover {
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.4);
  }
`;

const NodeHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 4px;
`;

const NodeIcon = styled.span`
  font-size: 16px;
`;

const NodeTitle = styled.strong`
  font-size: 13px;
  font-weight: 600;
`;

const NodeDescription = styled.div`
  font-size: 11px;
  opacity: 0.85;
  margin-top: 4px;
  word-wrap: break-word;
`;

const HandleStyle = {
  width: 10,
  height: 10,
  border: '2px solid #fff',
};

// Custom Node Components
const StartNode = ({ data, selected }) => {
  const style = nodeStyles.start;
  return (
    <NodeContainer background={style.background} borderColor={style.borderColor} style={{ border: selected ? '3px solid #fff' : undefined }}>
      <NodeHeader>
        <NodeIcon>{Icons.play}</NodeIcon>
        <NodeTitle>{data.label || 'Start'}</NodeTitle>
      </NodeHeader>
      {data.description && <NodeDescription>{data.description}</NodeDescription>}
      <Handle type="source" position={Position.Bottom} style={{ ...HandleStyle, background: style.borderColor }} />
    </NodeContainer>
  );
};

const QuestionNode = ({ data, selected }) => {
  const style = nodeStyles.question;
  return (
    <NodeContainer background={style.background} borderColor={style.borderColor} style={{ border: selected ? '3px solid #fff' : undefined }}>
      <Handle type="target" position={Position.Top} style={{ ...HandleStyle, background: style.borderColor }} />
      <NodeHeader>
        <NodeIcon>{Icons.question}</NodeIcon>
        <NodeTitle>{data.label || 'Question'}</NodeTitle>
      </NodeHeader>
      {data.questionText && <NodeDescription>{data.questionText}</NodeDescription>}
      <Handle type="source" position={Position.Bottom} style={{ ...HandleStyle, background: style.borderColor }} />
    </NodeContainer>
  );
};

const ConditionNode = ({ data, selected }) => {
  const style = nodeStyles.condition;
  return (
    <NodeContainer background={style.background} borderColor={style.borderColor} style={{ border: selected ? '3px solid #fff' : undefined }}>
      <Handle type="target" position={Position.Top} style={{ ...HandleStyle, background: style.borderColor }} />
      <NodeHeader>
        <NodeIcon>{Icons.gitBranch}</NodeIcon>
        <NodeTitle>{data.label || 'Condition'}</NodeTitle>
      </NodeHeader>
      {data.conditionValue && <NodeDescription>Check: {data.conditionValue}</NodeDescription>}
      {/* Yes handle on left, No handle on right */}
      <Handle 
        type="source" 
        position={Position.Left} 
        id="yes" 
        style={{ ...HandleStyle, background: '#10b981', top: '70%' }} 
      />
      <Handle 
        type="source" 
        position={Position.Right} 
        id="no" 
        style={{ ...HandleStyle, background: '#ef4444', top: '70%' }} 
      />
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '9px', marginTop: '8px', opacity: 0.8 }}>
        <span>✓ Yes</span>
        <span>✗ No</span>
      </div>
    </NodeContainer>
  );
};

const DocumentNode = ({ data, selected }) => {
  const style = nodeStyles.document;
  return (
    <NodeContainer background={style.background} borderColor={style.borderColor} style={{ border: selected ? '3px solid #fff' : undefined }}>
      <Handle type="target" position={Position.Top} style={{ ...HandleStyle, background: style.borderColor }} />
      <NodeHeader>
        <NodeIcon>{Icons.file}</NodeIcon>
        <NodeTitle>{data.label || 'Document'}</NodeTitle>
      </NodeHeader>
      {data.documentTypes?.length > 0 && <NodeDescription>Types: {data.documentTypes.join(', ')}</NodeDescription>}
      <Handle type="source" position={Position.Bottom} style={{ ...HandleStyle, background: style.borderColor }} />
    </NodeContainer>
  );
};

const AIAnalysisNode = ({ data, selected }) => {
  const style = nodeStyles['ai-analysis'];
  return (
    <NodeContainer background={style.background} borderColor={style.borderColor} style={{ border: selected ? '3px solid #fff' : undefined }}>
      <Handle type="target" position={Position.Top} style={{ ...HandleStyle, background: style.borderColor }} />
      <NodeHeader>
        <NodeIcon>{Icons.cpu}</NodeIcon>
        <NodeTitle>{data.label || 'AI Analysis'}</NodeTitle>
      </NodeHeader>
      {data.analysisPrompt && <NodeDescription>{data.analysisPrompt.substring(0, 50)}...</NodeDescription>}
      <Handle type="source" position={Position.Bottom} style={{ ...HandleStyle, background: style.borderColor }} />
    </NodeContainer>
  );
};

const OutcomeNode = ({ data, selected }) => {
  const style = nodeStyles.outcome;
  const outcomeColors = {
    success: '#10b981',
    failure: '#ef4444',
    pending: '#f59e0b',
    escalate: '#8b5cf6'
  };
  return (
    <NodeContainer 
      background={style.background} 
      borderColor={outcomeColors[data.outcomeType] || style.borderColor}
      style={{ border: selected ? '3px solid #fff' : undefined }}
    >
      <Handle type="target" position={Position.Top} style={{ ...HandleStyle, background: style.borderColor }} />
      <NodeHeader>
        <NodeIcon>{Icons.checkCircle}</NodeIcon>
        <NodeTitle>{data.label || 'Outcome'}</NodeTitle>
      </NodeHeader>
      {data.outcomeType && <NodeDescription>Type: {data.outcomeType}</NodeDescription>}
      {data.outcomeMessage && <NodeDescription>{data.outcomeMessage}</NodeDescription>}
    </NodeContainer>
  );
};

const ActionNode = ({ data, selected }) => {
  const style = nodeStyles.action;
  return (
    <NodeContainer background={style.background} borderColor={style.borderColor} style={{ border: selected ? '3px solid #fff' : undefined }}>
      <Handle type="target" position={Position.Top} style={{ ...HandleStyle, background: style.borderColor }} />
      <NodeHeader>
        <NodeIcon>{Icons.zap}</NodeIcon>
        <NodeTitle>{data.label || 'Action'}</NodeTitle>
      </NodeHeader>
      {data.actionType && <NodeDescription>Action: {data.actionType.replace(/_/g, ' ')}</NodeDescription>}
      <Handle type="source" position={Position.Bottom} style={{ ...HandleStyle, background: style.borderColor }} />
    </NodeContainer>
  );
};

// ============================================
// NODE TYPES
// ============================================

const nodeTypes = {
  start: StartNode,
  question: QuestionNode,
  condition: ConditionNode,
  document: DocumentNode,
  'ai-analysis': AIAnalysisNode,
  outcome: OutcomeNode,
  action: ActionNode,
};

// ============================================
// MAIN COMPONENT
// ============================================

const ScenarioFlowsModule = () => {
  const authenticatedAxios = useAuthenticatedAxios();
  const reactFlowWrapper = useRef(null);
  
  // AI Assistant context
  const { updateContext } = useAIAssistant();

  // State
  const [flows, setFlows] = useState([]);
  const [selectedFlow, setSelectedFlow] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isConverting, setIsConverting] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showTextToFlowModal, setShowTextToFlowModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [textToFlowInput, setTextToFlowInput] = useState('');
  const [newFlowName, setNewFlowName] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Selected node for properties panel
  const [selectedNode, setSelectedNode] = useState(null);
  const [selectedEdge, setSelectedEdge] = useState(null);

  // React Flow state
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [reactFlowInstance, setReactFlowInstance] = useState(null);

  // Update AI Assistant context when state changes
  useEffect(() => {
    updateContext({
      currentModule: 'scenario-flows',
      moduleName: 'Scenario Flows',
      moduleDescription: 'Visual flow builder for cases',
      selectedElements: {
        flow: selectedFlow ? {
          id: selectedFlow._id,
          name: selectedFlow.name,
          nodeCount: nodes.length,
          edgeCount: edges.length,
          status: selectedFlow.status
        } : null,
        node: selectedNode ? {
          id: selectedNode.id,
          type: selectedNode.type,
          label: selectedNode.data?.label,
          hasDescription: !!selectedNode.data?.description
        } : null,
        edge: selectedEdge ? {
          id: selectedEdge.id,
          label: selectedEdge.label
        } : null,
      },
      availableActions: [
        'createFlow',
        'generateFromText',
        ...(selectedFlow ? ['addNode', 'saveFlow', 'convertToRules', 'deleteFlow'] : []),
        ...(selectedNode ? ['editNode', 'deleteNode', 'connectNodes'] : []),
        ...(selectedEdge ? ['editEdge', 'deleteEdge'] : [])
      ],
      uiState: {
        flowCount: flows.length,
        isEditing: !!selectedFlow,
        showTextToFlowModal,
        isSaving,
        isGenerating
      }
    });
  }, [selectedFlow, selectedNode, selectedEdge, nodes, edges, flows.length, 
      showTextToFlowModal, isSaving, isGenerating, updateContext]);

  // Load flows on mount
  useEffect(() => {
    loadFlows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadFlows = async () => {
    try {
      setIsLoading(true);
      const response = await authenticatedAxios.get('/scenario-flows');
      setFlows(response.data.flows || []);
    } catch (error) {
      console.error('Error loading flows:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: date.getFullYear() !== new Date().getFullYear() ? 'numeric' : undefined
    });
  };

  // Load selected flow into editor
  const handleSelectFlow = async (flow) => {
    try {
      const response = await authenticatedAxios.get(`/scenario-flows/${flow._id}`);
      const fullFlow = response.data.flow;
      setSelectedFlow(fullFlow);
      setNodes(fullFlow.nodes || []);
      setEdges(fullFlow.edges || []);
      setSelectedNode(null);
      setSelectedEdge(null);
    } catch (error) {
      console.error('Error loading flow:', error);
    }
  };

  // Open create modal with suggested name
  const handleCreateNew = () => {
    // Generate unique name based on existing flows
    const existingNames = flows.map(f => f.name);
    let baseName = 'New Scenario Flow';
    let counter = 1;
    let uniqueName = baseName;
    
    while (existingNames.includes(uniqueName)) {
      counter++;
      uniqueName = `${baseName} ${counter}`;
    }
    
    setNewFlowName(uniqueName);
    setShowCreateModal(true);
  };

  // Actually create the new flow
  const handleConfirmCreate = async () => {
    const flowName = newFlowName.trim() || 'New Scenario Flow';
    
    const newFlow = {
      name: flowName,
      description: '',
      nodes: [{
        id: 'start-1',
        type: 'start',
        position: { x: 400, y: 100 },
        data: { label: 'Start' }
      }],
      edges: []
    };

    try {
      const response = await authenticatedAxios.post('/scenario-flows', newFlow);
      const createdFlow = response.data.flow;
      setFlows(prev => [createdFlow, ...prev]);
      handleSelectFlow(createdFlow);
      setShowCreateModal(false);
      setNewFlowName('');
    } catch (error) {
      console.error('Error creating flow:', error);
    }
  };

  // Save current flow
  const handleSave = async () => {
    if (!selectedFlow) return;
    setIsSaving(true);

    try {
      await authenticatedAxios.put(`/scenario-flows/${selectedFlow._id}`, {
        name: selectedFlow.name,
        description: selectedFlow.description,
        nodes,
        edges
      });
      await loadFlows();
    } catch (error) {
      console.error('Error saving flow:', error);
    } finally {
      setIsSaving(false);
    }
  };

  // Delete flow
  // Show delete confirmation modal
  const handleDeleteClick = () => {
    if (!selectedFlow) return;
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = async () => {
    if (!selectedFlow) return;
    setShowDeleteConfirm(false);

    try {
      await authenticatedAxios.delete(`/scenario-flows/${selectedFlow._id}`);
      ToastrService.Success(`Flow "${selectedFlow.name}" deleted successfully`);
      setSelectedFlow(null);
      setNodes([]);
      setEdges([]);
      setSelectedNode(null);
      await loadFlows();
    } catch (error) {
      console.error('Error deleting flow:', error);
      ToastrService.Error('Failed to delete flow. Please try again.');
    }
  };

  // Convert to rule set
  const handleConvertToRuleSet = async () => {
    if (!selectedFlow || isConverting) return;
    
    setIsConverting(true);
    
    try {
      // Save first
      await handleSave();
      
      const response = await authenticatedAxios.post(`/scenario-flows/${selectedFlow._id}/convert/to-ruleset`);
      ToastrService.Success(`Rule set "${response.data.ruleSet.name}" created! Find it in the Rule Sets module.`);
    } catch (error) {
      console.error('Error converting to rule set:', error);
      ToastrService.Error('Failed to convert to rule set. Please try again.');
    } finally {
      setIsConverting(false);
    }
  };

  // Text to flow conversion
  const handleTextToFlow = async () => {
    if (!textToFlowInput.trim()) return;
    setIsGenerating(true);

    try {
      const response = await authenticatedAxios.post('/scenario-flows/convert/text-to-flow', {
        text: textToFlowInput,
        name: newFlowName || 'Generated Flow'
      });
      
      const createdFlow = response.data.flow;
      setFlows(prev => [createdFlow, ...prev]);
      handleSelectFlow(createdFlow);
      setShowTextToFlowModal(false);
      setTextToFlowInput('');
      setNewFlowName('');
      ToastrService.Success(`Flow "${createdFlow.name}" generated successfully!`);
    } catch (error) {
      console.error('Error generating flow:', error);
      ToastrService.Error('Failed to generate flow. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  // Handle edge connection with labels
  const onConnect = useCallback(
    (params) => {
      const newEdge = {
        ...params,
        id: `edge-${Date.now()}`,
        animated: true,
        style: { stroke: '#6366f1', strokeWidth: 2 },
        markerEnd: { type: MarkerType.ArrowClosed, color: '#6366f1' },
      };

      // Add label for condition nodes
      if (params.sourceHandle === 'yes') {
        newEdge.label = 'Yes';
        newEdge.style = { stroke: '#10b981', strokeWidth: 2 };
        newEdge.markerEnd = { type: MarkerType.ArrowClosed, color: '#10b981' };
      } else if (params.sourceHandle === 'no') {
        newEdge.label = 'No';
        newEdge.style = { stroke: '#ef4444', strokeWidth: 2 };
        newEdge.markerEnd = { type: MarkerType.ArrowClosed, color: '#ef4444' };
      }

      setEdges((eds) => addEdge(newEdge, eds));
    },
    [setEdges]
  );

  // Add new node from toolbar
  const addNode = (type) => {
    if (!reactFlowInstance) return;
    
    // Get the center of the viewport
    const { x, y, zoom } = reactFlowInstance.getViewport();
    const centerX = (-x + 400) / zoom;
    const centerY = (-y + 300) / zoom;

    const newNode = {
      id: `${type}-${Date.now()}`,
      type,
      position: { x: centerX, y: centerY },
      data: { 
        label: type.charAt(0).toUpperCase() + type.slice(1).replace('-', ' '),
      },
    };

    setNodes((nds) => nds.concat(newNode));
    setSelectedNode(newNode);
  };

  // Delete selected node
  const deleteSelectedNode = () => {
    if (!selectedNode) return;
    
    setNodes((nds) => nds.filter((node) => node.id !== selectedNode.id));
    setEdges((eds) => eds.filter((edge) => edge.source !== selectedNode.id && edge.target !== selectedNode.id));
    setSelectedNode(null);
  };

  // Delete selected edge
  const deleteSelectedEdge = () => {
    if (!selectedEdge) return;
    
    setEdges((eds) => eds.filter((edge) => edge.id !== selectedEdge.id));
    setSelectedEdge(null);
  };

  // Handle node selection
  const onNodeClick = useCallback((event, node) => {
    setSelectedNode(node);
    setSelectedEdge(null);
  }, []);

  // Handle edge selection
  const onEdgeClick = useCallback((event, edge) => {
    setSelectedEdge(edge);
    setSelectedNode(null);
  }, []);

  // Handle pane click (deselect)
  const onPaneClick = useCallback(() => {
    setSelectedNode(null);
    setSelectedEdge(null);
  }, []);

  // Update node data
  const updateNodeData = (key, value) => {
    if (!selectedNode) return;

    setNodes((nds) =>
      nds.map((node) => {
        if (node.id === selectedNode.id) {
          const updatedNode = {
            ...node,
            data: { ...node.data, [key]: value },
          };
          setSelectedNode(updatedNode);
          return updatedNode;
        }
        return node;
      })
    );
  };

  // Filter flows
  const filteredFlows = flows.filter(flow => 
    flow.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    flow.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Render node properties based on type
  const renderNodeProperties = () => {
    if (!selectedNode) return null;

    const commonProps = (
      <>
        <PropertyGroup>
          <PropertyLabel>Label</PropertyLabel>
          <PropertyInput
            value={selectedNode.data?.label || ''}
            onChange={(e) => updateNodeData('label', e.target.value)}
            placeholder="Enter label..."
          />
        </PropertyGroup>
        <PropertyGroup>
          <PropertyLabel>Description</PropertyLabel>
          <PropertyTextarea
            value={selectedNode.data?.description || ''}
            onChange={(e) => updateNodeData('description', e.target.value)}
            placeholder="Enter description..."
          />
        </PropertyGroup>
      </>
    );

    switch (selectedNode.type) {
      case 'question':
        return (
          <>
            {commonProps}
            <PropertyGroup>
              <PropertyLabel>Question Text</PropertyLabel>
              <PropertyTextarea
                value={selectedNode.data?.questionText || ''}
                onChange={(e) => updateNodeData('questionText', e.target.value)}
                placeholder="What question should be asked?"
              />
            </PropertyGroup>
            <PropertyGroup>
              <PropertyLabel>Answer Type</PropertyLabel>
              <PropertySelect
                value={selectedNode.data?.expectedAnswerType || 'text'}
                onChange={(e) => updateNodeData('expectedAnswerType', e.target.value)}
              >
                <option value="text">Free Text</option>
                <option value="yes_no">Yes / No</option>
                <option value="multiple_choice">Multiple Choice</option>
                <option value="document">Document Upload</option>
              </PropertySelect>
            </PropertyGroup>
          </>
        );

      case 'condition':
        return (
          <>
            {commonProps}
            <PropertyGroup>
              <PropertyLabel>Condition Type</PropertyLabel>
              <PropertySelect
                value={selectedNode.data?.conditionType || 'contains'}
                onChange={(e) => updateNodeData('conditionType', e.target.value)}
              >
                <option value="contains">Contains</option>
                <option value="equals">Equals</option>
                <option value="greater_than">Greater Than</option>
                <option value="less_than">Less Than</option>
                <option value="regex">Regex Match</option>
                <option value="ai_evaluate">AI Evaluate</option>
              </PropertySelect>
            </PropertyGroup>
            <PropertyGroup>
              <PropertyLabel>Condition Value</PropertyLabel>
              <PropertyInput
                value={selectedNode.data?.conditionValue || ''}
                onChange={(e) => updateNodeData('conditionValue', e.target.value)}
                placeholder="Value to check..."
              />
            </PropertyGroup>
          </>
        );

      case 'document':
        return (
          <>
            {commonProps}
            <PropertyGroup>
              <PropertyLabel>Document Types (comma separated)</PropertyLabel>
              <PropertyInput
                value={selectedNode.data?.documentTypes?.join(', ') || ''}
                onChange={(e) => updateNodeData('documentTypes', e.target.value.split(',').map(s => s.trim()))}
                placeholder="e.g., passport, visa, pay stub"
              />
            </PropertyGroup>
            <PropertyGroup>
              <PropertyLabel>Analysis Prompt</PropertyLabel>
              <PropertyTextarea
                value={selectedNode.data?.analysisPrompt || ''}
                onChange={(e) => updateNodeData('analysisPrompt', e.target.value)}
                placeholder="What should AI look for in this document?"
              />
            </PropertyGroup>
          </>
        );

      case 'ai-analysis':
        return (
          <>
            {commonProps}
            <PropertyGroup>
              <PropertyLabel>AI Prompt</PropertyLabel>
              <PropertyTextarea
                value={selectedNode.data?.aiPrompt || ''}
                onChange={(e) => updateNodeData('aiPrompt', e.target.value)}
                placeholder="What should the AI analyze or decide?"
              />
            </PropertyGroup>
          </>
        );

      case 'outcome':
        return (
          <>
            {commonProps}
            <PropertyGroup>
              <PropertyLabel>Outcome Type</PropertyLabel>
              <PropertySelect
                value={selectedNode.data?.outcomeType || 'success'}
                onChange={(e) => updateNodeData('outcomeType', e.target.value)}
              >
                <option value="success">Success</option>
                <option value="failure">Failure</option>
                <option value="pending">Pending</option>
                <option value="escalate">Escalate</option>
              </PropertySelect>
            </PropertyGroup>
            <PropertyGroup>
              <PropertyLabel>Outcome Message</PropertyLabel>
              <PropertyTextarea
                value={selectedNode.data?.outcomeMessage || ''}
                onChange={(e) => updateNodeData('outcomeMessage', e.target.value)}
                placeholder="Message to show at this outcome..."
              />
            </PropertyGroup>
          </>
        );

      case 'action':
        return (
          <>
            {commonProps}
            <PropertyGroup>
              <PropertyLabel>Action Type</PropertyLabel>
              <PropertySelect
                value={selectedNode.data?.actionType || 'notify'}
                onChange={(e) => updateNodeData('actionType', e.target.value)}
              >
                <option value="send_email">Send Email</option>
                <option value="create_task">Create Task</option>
                <option value="update_case">Update Case</option>
                <option value="notify_expert">Notify Expert</option>
              </PropertySelect>
            </PropertyGroup>
          </>
        );

      default:
        return commonProps;
    }
  };

  if (isLoading) {
    return <ImavenLoadingScreen />;
  }

  return (
    <ModuleContainer>
      {/* Left Sidebar - Flow List */}
      <Sidebar>
        <SidebarHeader>
          <SidebarTitle>Scenario Flows</SidebarTitle>
          <SearchInput
            placeholder="Search flows..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </SidebarHeader>

        <FlowsList>
          {filteredFlows.length === 0 && (
            <div style={{ textAlign: 'center', color: 'rgba(255,255,255,0.4)', padding: '20px', fontSize: '13px' }}>
              No flows found
            </div>
          )}
          {filteredFlows.map((flow) => (
            <FlowCard 
              key={flow._id} 
              selected={selectedFlow?._id === flow._id}
              onClick={() => handleSelectFlow(flow)}
            >
              <FlowCardHeader>
                <FlowCardTitle>{flow.name}</FlowCardTitle>
                <FlowCardStatus status={flow.linkedRuleSet?.appliedToAgent ? 'complete' : flow.status}>
                  {flow.linkedRuleSet?.appliedToAgent ? 'complete' : flow.status}
                </FlowCardStatus>
              </FlowCardHeader>
              <FlowCardMeta>
                <span>{Icons.nodes} {flow.nodeCount ?? flow.nodes?.length ?? 0} nodes</span>
                <span>{Icons.link} {flow.edgeCount ?? flow.edges?.length ?? 0} links</span>
                <span>{Icons.calendar} {formatDate(flow.createdAt)}</span>
              </FlowCardMeta>
            </FlowCard>
          ))}
        </FlowsList>

        <SidebarButtons>
          <CreateNewButton onClick={handleCreateNew}>
            {Icons.plus} Create New Flow
          </CreateNewButton>
          <ActionButton 
            style={{ width: '100%', justifyContent: 'center' }}
            onClick={() => setShowTextToFlowModal(true)}
          >
            {Icons.sparkle} Generate from Text
          </ActionButton>
        </SidebarButtons>
      </Sidebar>

      {/* Flow Builder */}
      <FlowBuilderArea ref={reactFlowWrapper}>
        {selectedFlow ? (
          <>
            <FlowHeader>
              <FlowTitle
                value={selectedFlow.name}
                onChange={(e) => setSelectedFlow(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Flow name..."
              />
              <FlowActions>
                <ActionButton onClick={handleConvertToRuleSet} disabled={isConverting}>
                  {isConverting ? <SpinnerIcon>{Icons.loader}</SpinnerIcon> : Icons.list} {isConverting ? 'Converting...' : 'To Rules'}
                </ActionButton>
                <ActionButton danger onClick={handleDeleteClick} disabled={isConverting}>
                  {Icons.trash}
                </ActionButton>
                <ActionButton primary onClick={handleSave} disabled={isSaving || isConverting}>
                  {isSaving ? <SpinnerIcon>{Icons.loader}</SpinnerIcon> : Icons.save} {isSaving ? 'Saving...' : 'Save'}
                </ActionButton>
              </FlowActions>
            </FlowHeader>

            {/* Workflow Progress Banner */}
            <WorkflowBanner>
              <WorkflowSteps>
                <WorkflowStep $completed={nodes.length > 1} $current={nodes.length <= 1}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    {nodes.length > 1 ? <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14M22 4L12 14.01l-3-3"/> : <circle cx="12" cy="12" r="10"/>}
                  </svg>
                  1. Create Flow
                </WorkflowStep>
                <WorkflowArrow>→</WorkflowArrow>
                <WorkflowStep $completed={selectedFlow?.linkedRuleSetId} $current={nodes.length > 1 && !selectedFlow?.linkedRuleSetId}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    {selectedFlow?.linkedRuleSetId ? <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14M22 4L12 14.01l-3-3"/> : <circle cx="12" cy="12" r="10"/>}
                  </svg>
                  2. Convert to Rules
                </WorkflowStep>
                <WorkflowArrow>→</WorkflowArrow>
                <WorkflowStep $completed={!!selectedFlow?.linkedRuleSet?.generatedDirective} $current={selectedFlow?.linkedRuleSetId && !selectedFlow?.linkedRuleSet?.generatedDirective}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    {selectedFlow?.linkedRuleSet?.generatedDirective ? <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14M22 4L12 14.01l-3-3"/> : <circle cx="12" cy="12" r="10"/>}
                  </svg>
                  3. Generate Directive
                </WorkflowStep>
                <WorkflowArrow>→</WorkflowArrow>
                <WorkflowStep $completed={!!selectedFlow?.linkedRuleSet?.appliedToAgent} $current={selectedFlow?.linkedRuleSet?.generatedDirective && !selectedFlow?.linkedRuleSet?.appliedToAgent}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    {selectedFlow?.linkedRuleSet?.appliedToAgent ? <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14M22 4L12 14.01l-3-3"/> : <circle cx="12" cy="12" r="10"/>}
                  </svg>
                  4. Apply to Agent
                </WorkflowStep>
              </WorkflowSteps>
              {nodes.length > 1 && !selectedFlow?.linkedRuleSetId && (
                <WorkflowHint>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                  </svg>
                  Click "To Rules" to continue →
                </WorkflowHint>
              )}
            </WorkflowBanner>

            <ReactFlowWrapper>
              <ReactFlow
                nodes={nodes}
                edges={edges}
                onNodesChange={onNodesChange}
                onEdgesChange={onEdgesChange}
                onConnect={onConnect}
                onNodeClick={onNodeClick}
                onEdgeClick={onEdgeClick}
                onPaneClick={onPaneClick}
                onInit={setReactFlowInstance}
                nodeTypes={nodeTypes}
                fitView
                style={{ background: 'rgba(10, 15, 25, 0.95)' }}
                proOptions={{ hideAttribution: true }}
                defaultEdgeOptions={{
                  animated: true,
                  style: { stroke: '#6366f1', strokeWidth: 2 },
                  markerEnd: { type: MarkerType.ArrowClosed, color: '#6366f1' },
                }}
              >
                <Controls />
                <MiniMap 
                  pannable={true}
                  zoomable={true}
                  maskColor="rgba(15, 23, 42, 0.85)"
                  style={{
                    background: 'rgba(30, 41, 59, 0.95)',
                    border: '1px solid rgba(99, 102, 241, 0.3)',
                  }}
                  nodeColor={(node) => {
                    const colors = {
                      start: '#10b981',
                      question: '#3b82f6',
                      condition: '#f59e0b',
                      document: '#8b5cf6',
                      'ai-analysis': '#ec4899',
                      outcome: '#6366f1',
                      action: '#14b8a6',
                    };
                    return colors[node.type] || '#6366f1';
                  }}
                />
                <Background variant="dots" gap={20} size={1} color="rgba(99, 102, 241, 0.2)" />

                {/* Toolbar */}
                <Toolbar>
                  <ToolbarButton onClick={() => addNode('question')}>{Icons.question} Question</ToolbarButton>
                  <ToolbarButton onClick={() => addNode('condition')}>{Icons.gitBranch} Condition</ToolbarButton>
                  <ToolbarButton onClick={() => addNode('document')}>{Icons.file} Document</ToolbarButton>
                  <ToolbarButton onClick={() => addNode('ai-analysis')}>{Icons.cpu} AI</ToolbarButton>
                  <ToolbarButton onClick={() => addNode('outcome')}>{Icons.checkCircle} Outcome</ToolbarButton>
                  <ToolbarButton onClick={() => addNode('action')}>{Icons.zap} Action</ToolbarButton>
                  <ToolbarDivider />
                  {selectedNode && (
                    <ToolbarButton onClick={deleteSelectedNode} style={{ color: '#ef4444' }}>
                      {Icons.trash} Delete Node
                    </ToolbarButton>
                  )}
                  {selectedEdge && (
                    <ToolbarButton onClick={deleteSelectedEdge} style={{ color: '#ef4444' }}>
                      {Icons.trash} Delete Link
                    </ToolbarButton>
                  )}
                </Toolbar>
              </ReactFlow>
            </ReactFlowWrapper>
          </>
        ) : (
          <EmptyState>
            <EmptyStateIcon>{Icons.gitBranch}</EmptyStateIcon>
            <EmptyStateText>Select a scenario flow or create a new one</EmptyStateText>
            <EmptyStateSubtext>Build visual decision trees for your AI agent</EmptyStateSubtext>
          </EmptyState>
        )}
      </FlowBuilderArea>

      {/* Right Sidebar - Properties Panel */}
      {selectedFlow && (
        <PropertiesPanel>
          <PropertiesHeader>
            <PropertiesTitle>
              {selectedNode ? (
                <>
                  {nodeIcons[selectedNode.type] || Icons.pin} {selectedNode.type?.charAt(0).toUpperCase() + selectedNode.type?.slice(1).replace('-', ' ')} Properties
                </>
              ) : selectedEdge ? (
                <>{Icons.link} Connection Properties</>
              ) : (
                <>{Icons.settings} Properties</>
              )}
            </PropertiesTitle>
            {(selectedNode || selectedEdge) && (
              <CloseButton onClick={() => { setSelectedNode(null); setSelectedEdge(null); }}>
                {Icons.x} Close
              </CloseButton>
            )}
          </PropertiesHeader>

          <PropertiesContent>
            {selectedNode ? (
              <>
                {renderNodeProperties()}
                <DeleteNodeButton onClick={deleteSelectedNode}>
                  {Icons.trash} Delete This Node
                </DeleteNodeButton>
              </>
            ) : selectedEdge ? (
              <>
                <PropertyGroup>
                  <PropertyLabel>Connection Label</PropertyLabel>
                  <PropertyInput
                    value={selectedEdge.label || ''}
                    onChange={(e) => {
                      setEdges((eds) =>
                        eds.map((edge) =>
                          edge.id === selectedEdge.id
                            ? { ...edge, label: e.target.value }
                            : edge
                        )
                      );
                      setSelectedEdge(prev => ({ ...prev, label: e.target.value }));
                    }}
                    placeholder="e.g., Yes, No, Next..."
                  />
                </PropertyGroup>
                <DeleteNodeButton onClick={deleteSelectedEdge}>
                  {Icons.trash} Delete This Connection
                </DeleteNodeButton>
              </>
            ) : (
              <NoPropertiesMessage>
                <p>Click on a node or connection to edit its properties</p>
                <p style={{ marginTop: '20px' }}>
                  <strong>Tips:</strong>
                </p>
                <ul style={{ textAlign: 'left', marginTop: '10px', paddingLeft: '20px' }}>
                  <li>Use the toolbar to add nodes</li>
                  <li>Drag from handles to connect nodes</li>
                  <li>Conditions have Yes/No handles</li>
                  <li>Click nodes to edit content</li>
                </ul>
              </NoPropertiesMessage>
            )}
          </PropertiesContent>
        </PropertiesPanel>
      )}

      {/* Text to Flow Modal */}
      {showTextToFlowModal && (
        <ModalOverlay onClick={() => setShowTextToFlowModal(false)}>
          <ModalContent onClick={e => e.stopPropagation()}>
            <ModalTitle>{Icons.sparkle} Generate Flow from Text</ModalTitle>
            
            <ModalLabel>Flow Name</ModalLabel>
            <ModalInput
              value={newFlowName}
              onChange={(e) => setNewFlowName(e.target.value)}
              placeholder="Enter a name for this flow..."
            />

            <ModalLabel>Describe your scenario</ModalLabel>
            <ModalTextarea
              value={textToFlowInput}
              onChange={(e) => setTextToFlowInput(e.target.value)}
              placeholder="Example: When a user asks about work permits, first check if they have previous work experience in the country. If yes, ask about their job role. If the job was in a regulated profession, request documentation..."
            />

            <ModalButtons>
              <ActionButton onClick={() => setShowTextToFlowModal(false)}>
                Cancel
              </ActionButton>
              <ActionButton 
                primary 
                onClick={handleTextToFlow}
                disabled={isGenerating || !textToFlowInput.trim()}
              >
                {isGenerating ? <>{Icons.loader} Generating...</> : <>{Icons.sparkle} Generate Flow</>}
              </ActionButton>
            </ModalButtons>
          </ModalContent>
        </ModalOverlay>
      )}

      {/* Create New Flow Modal */}
      {showCreateModal && (
        <ModalOverlay onClick={() => setShowCreateModal(false)}>
          <ModalContent onClick={e => e.stopPropagation()} style={{ maxWidth: '400px' }}>
            <ModalTitle>{Icons.plus} Create New Scenario Flow</ModalTitle>
            
            <ModalLabel>Flow Name</ModalLabel>
            <ModalInput
              value={newFlowName}
              onChange={(e) => setNewFlowName(e.target.value)}
              placeholder="Enter a name for your flow..."
              autoFocus
              onKeyPress={(e) => e.key === 'Enter' && handleConfirmCreate()}
            />

            <ModalButtons>
              <ActionButton onClick={() => setShowCreateModal(false)}>
                Cancel
              </ActionButton>
              <ActionButton 
                primary 
                onClick={handleConfirmCreate}
                disabled={!newFlowName.trim()}
              >
                {Icons.plus} Create Flow
              </ActionButton>
            </ModalButtons>
          </ModalContent>
        </ModalOverlay>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <ConfirmOverlay onClick={() => setShowDeleteConfirm(false)}>
          <ConfirmModal onClick={e => e.stopPropagation()}>
            <ConfirmIcon>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="3 6 5 6 21 6"/>
                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                <line x1="10" y1="11" x2="10" y2="17"/>
                <line x1="14" y1="11" x2="14" y2="17"/>
              </svg>
            </ConfirmIcon>
            <ConfirmTitle>Delete Scenario Flow?</ConfirmTitle>
            <ConfirmMessage>
              Are you sure you want to delete <ConfirmItemName>"{selectedFlow?.name}"</ConfirmItemName>? 
              This action cannot be undone.
            </ConfirmMessage>
            <ConfirmButtons>
              <ConfirmButton onClick={() => setShowDeleteConfirm(false)}>
                Cancel
              </ConfirmButton>
              <ConfirmButton $danger onClick={handleConfirmDelete}>
                Delete
              </ConfirmButton>
            </ConfirmButtons>
          </ConfirmModal>
        </ConfirmOverlay>
      )}
    </ModuleContainer>
  );
};

export default ScenarioFlowsModule;
